import {
  AllContent,
  AllFeatures,
  AllPrograms,
  RawOtpBundle,
} from '../../common/bundle.types';
import {
  faAlignLeft,
  faShareNodes,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { faCopy } from '@fortawesome/free-regular-svg-icons';
import { BundleItemType, BundleStatus } from 'generated/graphql';
import { ColorsVars } from '@design-system/vars.css';
import {
  BundleCardItem,
  Submitting,
  BundlesVMDispatcher,
  Standard,
} from './otpBundles.viewModel';
import { ButtonProps } from '@design-system/components/button/button';
import { ButtonStylingProps } from '@design-system/components/button/helper';
import { PATHS } from '@client/routes';
import { collectionLinks } from '@client/new/pages/collections/collection-links';
import {
  EditBundleSubmit,
  NewBundleSubmit,
  OtpBundlesModel,
} from '../model/otpBundles.model';
import { isEmpty, noop } from 'lodash';

export const statusColor = {
  ARCHIVED: {
    bg: 'ERROR-20' as ColorsVars,
    text: 'ERROR-100' as ColorsVars,
  },
  DRAFT: {
    bg: 'D-20' as ColorsVars,
    text: 'D-100' as ColorsVars,
  },
  PUBLISHED: {
    bg: 'G-80' as ColorsVars,
    text: 'W-100' as ColorsVars,
  },
};

export const toProgramDetails = (
  allPrograms: AllPrograms,
  bundleItems?: RawOtpBundle['items'],
) => {
  const mappedPrograms = (bundleItems || [])
    .filter((item) => item.itemType === BundleItemType.Program)
    .map((item) => ({
      title: allPrograms.find((p) => p.id === item.itemId)?.name || '',
    }));

  return mapItemsToUi(mappedPrograms);
};

export const toContentDetails = (
  allContent: AllContent,
  bundleItems?: RawOtpBundle['items'],
) => {
  const mappedContent = (bundleItems || [])
    .filter((item) => item.itemType === BundleItemType.Content)
    .map((item) => ({
      title: allContent.find((p) => p.id === item.contentId)?.name || '',
    }));

  return mapItemsToUi(mappedContent);
};

const mapItemsToUi = (items: Array<{ title: string }>) => {
  const result = [];
  const maxRowChars = 40;
  let currentLength = 0;
  let currentIndex = 0;

  for (; currentIndex < items.length; currentIndex++) {
    const item = items[currentIndex];
    if (currentLength + item.title.length > maxRowChars) {
      break;
    }
    result.push(item);
    currentLength += item.title.length;
  }

  if (currentIndex === items.length) return result;

  const remainingItems = items.slice(currentIndex).map((i) => i.title);

  return [
    ...result,
    { title: `+${items.length - currentIndex}`, tooltipItems: remainingItems },
  ];
};

export const toFormFeatures = (
  allFeatures: AllFeatures,
  selectedFeatures: number[],
  dispatch: BundlesVMDispatcher,
) =>
  allFeatures.map((f) => ({
    id: f.id,
    name: f.name,
    checked: selectedFeatures.includes(f.id),
    onChange: () =>
      dispatch('toggleBundleContent', {
        itemId: f.id,
        type: BundleItemType.Feature,
      }),
  }));

export const toFormPrograms = (
  allPrograms: AllPrograms,
  selectedPrograms: number[],
  dispatch: BundlesVMDispatcher,
) =>
  allPrograms.map((p) => ({
    id: p.id,
    name: p.name,
    checked: selectedPrograms.includes(p.id),
    onChange: () =>
      dispatch('toggleBundleContent', {
        itemId: p.id,
        type: BundleItemType.Program,
      }),
  }));

export const toFormSelectedPrograms = (
  allPrograms: AllPrograms,
  form: OtpBundlesModel['form'],
  dispatch: BundlesVMDispatcher,
) =>
  allPrograms
    .filter((p) => form.data.programs.includes(p.id))
    .map((p) => ({
      id: p.id,
      name: p.name,
      cta:
        form.wasPurchased && form.savedProgramIds.includes(p.id)
          ? []
          : [
              {
                icon: faTrash,
                onClick: () =>
                  dispatch('removeBundleContent', {
                    itemId: p.id,
                    type: BundleItemType.Program,
                  }),
              },
            ],
    }));

export const toFormContent = (
  allContent: AllContent,
  selectedContent: string[],
  dispatch: BundlesVMDispatcher,
) =>
  allContent.map((c) => ({
    id: c.id,
    name: c.name,
    checked: selectedContent.includes(c.id),
    onChange: () =>
      dispatch('toggleBundleContent', {
        contentId: c.id,
        type: BundleItemType.Content,
      }),
  }));

export const toFormSelectedContent = (
  allContent: AllContent,
  form: OtpBundlesModel['form'],
  dispatch: BundlesVMDispatcher,
) =>
  allContent
    .filter((c) => form.data.content.includes(c.id))
    .map((p) => ({
      id: p.id,
      name: p.name,
      cta:
        form.wasPurchased && form.savedContentIds.includes(p.id)
          ? []
          : [
              {
                icon: faTrash,
                onClick: () =>
                  dispatch('removeBundleContent', {
                    contentId: p.id,
                    type: BundleItemType.Content,
                  }),
              },
            ],
    }));

export const toBudleDetails = (items?: RawOtpBundle['items']) => {
  const programsCount = (items || []).filter(
    (i) => i.itemType === BundleItemType.Program,
  ).length;

  const contentCount = (items || []).filter(
    (i) => i.itemType === BundleItemType.Content,
  ).length;

  if (programsCount > 0 && contentCount > 0)
    return `${programsCount} ${
      programsCount === 1 ? 'Program' : 'Programs'
    } • ${contentCount} ${contentCount === 1 ? 'Item' : 'Items'}`;
  if (programsCount > 0)
    return `${programsCount} ${programsCount === 1 ? 'Program' : 'Programs'}`;
  if (contentCount > 0)
    return `${contentCount} ${contentCount === 1 ? 'Item' : 'Items'}`;
  return '';
};

export const filterBundles = (filter: string, bundles: RawOtpBundle[]) => {
  switch (filter) {
    case 'All':
      return bundles;
    case 'Drafts':
      return bundles.filter((b) => b.status === BundleStatus.Draft);
    case 'Published':
      return bundles.filter((b) => b.status === BundleStatus.Published);
    case 'Archived':
      return bundles.filter((b) => b.status === BundleStatus.Archived);
    default:
      return bundles;
  }
};

export const toOTPBundleItems = (
  otpBundles: RawOtpBundle[],
  allPrograms: AllPrograms,
  allFeatures: AllFeatures,
  allContent: AllContent,
  dispatch: BundlesVMDispatcher,
  submittedNewBundle: NewBundleSubmit,
  submittedEditBundle: EditBundleSubmit,
): Array<BundleCardItem> => {
  // Sort bundles by ID in descending order (newest first)
  const sortedBundles = [...otpBundles].sort((a, b) => b.id - a.id);

  const existingBundles = sortedBundles.map((bundle) => ({
    onClick: () => dispatch('editBundle', bundle.id),
    menu: [
      {
        icon: faAlignLeft,
        disabled: !bundle.description,
        tooltip: bundle.description,
      },
      {
        icon: faShareNodes,
        // disabled: bundle.status === BundleStatus.Archived,
        // onClick: () => dispatch('openLinksModal', bundle.id),
        disabled: true, // To be enabled before release on November 24th
        tooltip: [
          'Coming soon!',
          'You will be able to see your offers links on November 24th when this feature becomes officially released. 🎉',
        ],
      },

      {
        icon: faCopy,
        disabled: false,
        onClick: () => dispatch('duplicateBundle', bundle.id),
      },
    ],
    data: toBundleCardData(
      bundle,
      allPrograms,
      allFeatures,
      allContent,
      submittedEditBundle,
    ),
  }));

  return [
    ...existingBundles,
    ...toBundleCardSubmittedItems(submittedNewBundle),
  ];
};

const toBundleCardSubmittedItems = (
  submittedNewBundle: NewBundleSubmit,
): Array<BundleCardItem> => {
  if (isEmpty(submittedNewBundle)) return [];

  return submittedNewBundle.map((i) => ({
    onClick: noop,
    menu: [],
    data: {
      type: 'submitting' as Submitting,
      title: i.title,
    },
  }));
};

const toBundleCardData = (
  bundle: RawOtpBundle,
  allPrograms: AllPrograms,
  allFeatures: AllFeatures,
  allContent: AllContent,
  submittedEditBundle: EditBundleSubmit,
) => {
  if (submittedEditBundle.some((b) => b.id === bundle.id)) {
    return {
      type: 'submitting' as Submitting,
      title: bundle.title,
    };
  }

  return {
    type: 'standard' as Standard,
    title: bundle.title,
    price: bundle?.oneTimeProduct?.price
      ? `$ ${bundle?.oneTimeProduct?.price / 100}`
      : '',
    details: toBudleDetails(bundle?.items),
    programs: toProgramDetails(allPrograms, bundle?.items),
    status: {
      label: bundle.status.toLowerCase(),
      color: statusColor[bundle.status],
    },
    content: toContentDetails(allContent, bundle?.items),
    features: toOTPBundleItemFeatures(allFeatures, bundle),
  };
};

const toOTPBundleItemFeatures = (
  allFeatures: AllFeatures,
  bundle?: RawOtpBundle,
) =>
  allFeatures.map((f) => ({
    id: f.id,
    name: f.name,
    checked: !bundle
      ? false
      : !!(bundle?.items || []).find(
          (i) => i.itemType === BundleItemType.Feature && i.itemId === f.id,
        ),
  }));

type Cta = {
  label: string;
  onClick: () => void;
  color?: ButtonProps['color'];
  variant?: ButtonStylingProps['variant'];
  className?: string;
  disabled?: boolean;
  infoText?: string;
  tooltip?: string[];
};

export type FormCta = {
  header: Array<Cta>;
  footer: Array<Cta>;
};

export const toFormCta = (
  editMode: boolean,
  dispatch: BundlesVMDispatcher,
  published: boolean,
  readonly: boolean,
  bundleId: number | null,
  wasPurchased: boolean,
): FormCta => ({
  header:
    readonly && bundleId
      ? [
          {
            label: 'Duplicate',
            onClick: () => dispatch('duplicateBundle', bundleId),
          },
        ]
      : published
      ? [
          {
            label: 'Save',
            onClick: () => dispatch('saveBundle'),
            color: 'primaryTonal',
          },
        ]
      : [
          {
            label: editMode ? 'Save' : 'Save as draft',
            onClick: () => dispatch('saveBundle'),
            color: 'primaryTonal',
          },
          {
            label: 'Publish',
            onClick: () => dispatch('publishBundle'),
            disabled: true, // To be enabled before release on November 24th
            tooltip: [
              'Coming soon!',
              'You will be able to publish all your offers on November 24th when this feature becomes officially released. 🎉',
            ],
          },
        ],
  footer: readonly
    ? []
    : editMode
    ? [
        {
          label: 'Edit landing page',
          onClick: () => dispatch('editLandingPage'),
          color: 'primary',
          variant: 'text',
          className: 'w-max',
        },
        {
          label: 'Archive',
          onClick: () => dispatch('archiveBundle'),
          color: 'neutral',
          variant: 'text',
          className: 'w-full',
        },
        {
          label: 'Delete',
          onClick: () => dispatch('deleteBundle'),
          color: 'errorTonal',
          disabled: !bundleId || wasPurchased,
          className: 'w-full',
        },
      ]
    : [
        {
          label: 'Edit landing page',
          onClick: () => dispatch('editLandingPage'),
          color: 'primary',
          variant: 'text',
          className: 'w-max',
          disabled: true,
          infoText:
            "You'll be able to edit the landing page once you've created and saved your offer",
        },
      ],
});
export const toFormProgramsEmptyState = (
  dispatch: BundlesVMDispatcher,
  draftMode: boolean,
) => ({
  urlLabel: 'Create your first program',
  label: 'in order to be able to add them to your bundle',
  onClick: () =>
    draftMode
      ? dispatch('confirmRedirect', 'program')
      : dispatch('saveAndRedirect', { saveChanges: false }),
});

export const toFormContentEmptyState = (
  dispatch: BundlesVMDispatcher,
  draftMode: boolean,
) => ({
  urlLabel: 'Create your first content item',
  label: 'in order to be able to add them to your bundle',
  onClick: () =>
    draftMode
      ? dispatch('confirmRedirect', 'content')
      : dispatch('saveAndRedirect', { saveChanges: false }),
});

export type ActiveTab = 'All' | 'Published' | 'Drafts' | 'Archived';

export type ListFilter = {
  label: string;
  value: string;
  badge: number;
};

export const toListFilterTabs = (
  bundles: RawOtpBundle[],
): Array<ListFilter> => [
  {
    label: 'All',
    value: 'All',
    badge: bundles.length,
  },
  {
    label: 'Published',
    value: 'Published',
    badge: bundles.filter((b) => b.status === BundleStatus.Published).length,
  },
  {
    label: 'Drafts',
    value: 'Drafts',
    badge: bundles.filter((b) => b.status === BundleStatus.Draft).length,
  },
  {
    label: 'Archived',
    value: 'Archived',
    badge: bundles.filter((b) => b.status === BundleStatus.Archived).length,
  },
];

export const getOfferLinks = (
  dispatch: BundlesVMDispatcher,
  bundle?: RawOtpBundle,
) => {
  if (!bundle?.id) {
    return [];
  }

  return [
    {
      ...toCollectionLink('offerLandingPage'),
      url: bundle.landingPageUrl,
      copy: () => dispatch('copyToClipboard', bundle.landingPageUrl),
      edit: () => dispatch('editLandingPage'),
    },
    {
      ...toCollectionLink('offerCheckoutPage'),
      url: bundle.checkoutPage.url,
      copy: () => dispatch('copyToClipboard', bundle.checkoutPage.url),
    },
    {
      ...toCollectionLink('offerDeepLink'),
      url: bundle.deeplinkUrl,
      copy: () => dispatch('copyToClipboard', bundle.deeplinkUrl),
    },
  ].filter((l) => l.url);
};

export const toCollectionLink = (
  type: keyof typeof collectionLinks,
): { title: string; description: string } => {
  const link = collectionLinks[type];
  return {
    title: link.title,
    description: link.description,
  };
};
